import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { currentStep } from '@/app/store'
import { useAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { transactionsService } from '@/app/services/transactionService'
import { CustomerData, useCrefazContext } from '@/app/contexts/CrefazContext'
import { useToast } from '@/view/components/ui/use-toast'
import { customerService } from '@/app/services/customerService'
import { createCustomerBody } from '@/app/services/customerService/createCustomer'
import BasicOptions from '@/view/components/BasicOptions'
import { BasicSelect } from '@/view/components/BasicSelect'
import { BasicInput } from '@/view/components/BasicInput'
import { Button } from '@/view/components/ui/button'
import { Loader2 } from 'lucide-react'

export const bankFormSchema = z.object({
  accountType: z
    .string({ required_error: '* obrigatório' })
    .min(1, '* obrigatório'),
  bank: z.string({ required_error: '* obrigatório' }).min(1, '* obrigatório'),
  agency: z.string().min(3, '* obrigatório').max(5, 'No maximo 5 caracteres'),
  account: z.string().min(4, '* obrigatório'),
})

interface BankInfoFormProps {
  visible?: boolean
}
export default function BankInfoForm({ visible }: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep)
  const { customerData, setCustomerData, proposalData, transactionId } =
    useCrefazContext()
  const { toast } = useToast()

  const {
    handleSubmit,
    formState: { errors },
  } = useFormContext()

  const { data: banks, isLoading } = useQuery({
    queryKey: ['banks', 'CREFAZ'],
    queryFn: () => transactionsService.banks({ type: 'CREFAZ' }),
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }))
    },
  })

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)

  const onSubmit = async (data: any) => {
    if (!customerData) return

    setIsLoadingSubmit(true)

    const updatedCustomerData: CustomerData = {
      ...customerData,
      ...data.personalInfo,
      address: data.address,
      referenceContact: data.contactReferences.referenceContact,
      bankAccount: data.bankAccount,
      ocupacaoId: customerData.ocupacaoId,
      renda: customerData.renda,
      diaRecebimento: proposalData?.diaRecebimento?.toString(),
    }

    try {
      const response = await customerService.createCustomer(
        updatedCustomerData as createCustomerBody,
      )

      setCustomerData({
        ...updatedCustomerData,
        id: response._id,
      })

      if (transactionId) {
        await transactionsService.editTransaction({
          transactionId,
          crefazValidation: true,
        })
      }

      setStep((state) => state + 1)
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar cliente',
        description:
          error?.message || 'Erro inesperado. Tente novamente mais tarde.',
      })
    } finally {
      setIsLoadingSubmit(false)
    }
  }

  return (
    <div className={visible ? 'flex flex-col gap-8' : 'hidden'}>
      <div className="grid grid-cols-inputs gap-y-4">
        <BasicOptions
          options={[
            { label: 'Poupança', value: 'CP' },
            { label: 'Corrente', value: 'CC' },
          ]}
          name="bankAccount.accountType"
          label="Tipo de conta"
          error={(errors.bankAccount as any)?.accountType?.message}
        />

        <div />

        {!isLoading && (
          <div className="mr-4">
            <BasicSelect
              name="bankAccount.bank"
              label="Banco"
              error={(errors.bankAccount as any)?.bank?.message}
              options={banks || []}
              placeholder="Selecione seu banco"
              tabIndex={6}
            />
          </div>
        )}

        <div className="mr-8 grid grid-cols-2 gap-4">
          <BasicInput
            tabIndex={0}
            name="bankAccount.agency"
            label="Agencia"
            error={(errors.bankAccount as any)?.agency?.message}
            type="number"
            maxLength={4}
          />
          <BasicInput
            tabIndex={0}
            name="bankAccount.account"
            label="Conta"
            error={(errors.bankAccount as any)?.account?.message}
            type="number"
          />
        </div>
      </div>
      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
          disabled={isLoadingSubmit}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoadingSubmit}
        >
          {isLoadingSubmit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Continuar
        </Button>
      </div>
    </div>
  )
}
